import type { MeasurementRow } from "@/components/tools/math/measurement-rows";
import {
  general,
  type PropagationView,
} from "@/components/tools/math/uncertainty-results";
import type { UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS } from "@/components/tools/uncertainty-propagation-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleUnit,
  type ExprError,
  formatExpression,
  parseExpression,
} from "@/lib/tool-kit/math/expression";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import {
  type Measured,
  propagateFormula,
  propagatePower,
  propagateProduct,
  propagateSum,
} from "@/lib/tool-kit/math/uncertainty";

/*
 * Turn each mode's typed inputs into a result view — values, contributions and the
 * working as lines of mathematics — or into the message that explains what is missing.
 * The arithmetic itself is in `@/lib/tool-kit/math/uncertainty`; this file only reads
 * input and writes the working.
 */

type Strings = typeof UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export type ViewOutcome =
  | { kind: "view"; view: PropagationView }
  | { kind: "notice"; message: string }
  | { kind: "error"; message: string };

interface ReadRow {
  row: MeasurementRow;
  label: string;
  measured: Measured;
}

/** Each row's numbers, or the reason they cannot be used yet. */
function readRows(
  rows: readonly MeasurementRow[],
  strings: Strings
): ReadRow[] | ViewOutcome {
  const read: ReadRow[] = [];
  for (const [index, row] of rows.entries()) {
    const value = parseMeasuredNumber(row.value);
    const uncertainty = parseMeasuredNumber(row.uncertainty);
    if (value === null || uncertainty === null) {
      return { kind: "notice", message: strings.enterAll };
    }
    if (uncertainty < 0) {
      return { kind: "error", message: strings.negativeUncertainty };
    }
    read.push({
      row,
      label: t(strings.rowLabel, { n: index + 1 }),
      measured: { value, uncertainty },
    });
  }
  return read;
}

const LEADING_SIGN = /^[+\-−]\s*/;

/** A typed number without its sign: what |x| looks like in the working. */
function magnitudeText(typed: string): string {
  return typed.trim().replace(LEADING_SIGN, "");
}

function squaresLine(fmt: Format, parts: readonly number[]): string {
  return parts.map((p) => `${general(fmt, p)}²`).join(" + ");
}

export function sumView(
  rows: readonly MeasurementRow[],
  strings: Strings,
  fmt: Format
): ViewOutcome {
  const read = readRows(rows, strings);
  if (!Array.isArray(read)) {
    return read;
  }
  const result = propagateSum(
    read.map((r) => ({ sign: r.row.op === "-" ? -1 : 1, measured: r.measured }))
  );
  const expression = read
    .map((r, i) => {
      const value = r.row.value.trim();
      if (i === 0) {
        return r.row.op === "-" ? `−${value}` : value;
      }
      return `${r.row.op === "-" ? "−" : "+"} ${value}`;
    })
    .join(" ");
  const variance = result.uncertainty ** 2;
  return {
    kind: "view",
    view: {
      ...result,
      contributions: read.map((r, i) => ({
        ...result.contributions[i],
        label: r.label,
        valueText: r.row.value.trim(),
        uncertaintyText: r.row.uncertainty.trim(),
        nonlinear: false,
      })),
      working: [
        `q = ${expression} = ${general(fmt, result.value)}`,
        `δq = √(${squaresLine(
          fmt,
          read.map((r) => r.measured.uncertainty)
        )}) = √${general(fmt, variance)} = ${general(fmt, result.uncertainty)}`,
      ],
    },
  };
}

export function productView(
  rows: readonly MeasurementRow[],
  strings: Strings,
  fmt: Format
): ViewOutcome {
  const read = readRows(rows, strings);
  if (!Array.isArray(read)) {
    return read;
  }
  const result = propagateProduct(
    read.map((r) => ({
      op: r.row.op === "/" ? "divide" : "multiply",
      measured: r.measured,
    }))
  );
  if (result === null) {
    return { kind: "error", message: strings.productZero };
  }
  const expression = read
    .map((r, i) => {
      const value = r.row.value.trim();
      if (i === 0) {
        return r.row.op === "/" ? `1 ÷ ${value}` : value;
      }
      return `${r.row.op === "/" ? "÷" : "×"} ${value}`;
    })
    .join(" ");
  const relatives = result.contributions.map((c) => c.relative ?? 0);
  const ratios = read
    .map(
      (r) => `(${r.row.uncertainty.trim()} / ${magnitudeText(r.row.value)})²`
    )
    .join(" + ");
  const relative = result.relative ?? 0;
  return {
    kind: "view",
    view: {
      ...result,
      contributions: read.map((r, i) => ({
        ...result.contributions[i],
        label: r.label,
        valueText: r.row.value.trim(),
        uncertaintyText: r.row.uncertainty.trim(),
        nonlinear: false,
      })),
      working: [
        `q = ${expression} = ${general(fmt, result.value)}`,
        `δq / |q| = √(${ratios}) = √(${squaresLine(fmt, relatives)}) = ${general(fmt, relative)}`,
        `δq = |q| × ${general(fmt, relative)} = ${general(fmt, Math.abs(result.value))} × ${general(fmt, relative)} = ${general(fmt, result.uncertainty)}`,
      ],
    },
  };
}

export function powerView(
  inputs: { x: string; dx: string; n: string },
  strings: Strings,
  fmt: Format
): ViewOutcome {
  const x = parseMeasuredNumber(inputs.x);
  const dx = parseMeasuredNumber(inputs.dx);
  const n = parseMeasuredNumber(inputs.n);
  if (x === null || dx === null || n === null) {
    return { kind: "notice", message: strings.enterAll };
  }
  if (dx < 0) {
    return { kind: "error", message: strings.negativeUncertainty };
  }
  const result = propagatePower({ value: x, uncertainty: dx }, n);
  if (result === null) {
    return { kind: "error", message: strings.powerDomain };
  }
  const relative = result.relative ?? 0;
  return {
    kind: "view",
    view: {
      ...result,
      contributions: [
        {
          ...result.contributions[0],
          label: "x",
          valueText: inputs.x.trim(),
          uncertaintyText: inputs.dx.trim(),
          nonlinear: false,
        },
      ],
      working: [
        `q = xⁿ = (${inputs.x.trim()})^${inputs.n.trim()} = ${general(fmt, result.value)}`,
        `δq / |q| = |n| × δx / |x| = ${magnitudeText(inputs.n)} × ${inputs.dx.trim()} / ${magnitudeText(inputs.x)} = ${general(fmt, relative)}`,
        `δq = |q| × ${general(fmt, relative)} = ${general(fmt, Math.abs(result.value))} × ${general(fmt, relative)} = ${general(fmt, result.uncertainty)}`,
      ],
    },
  };
}

export interface VariableInput {
  value: string;
  uncertainty: string;
}

function parseMessage(error: ExprError, strings: Strings): string {
  return t(strings.expressionErrors[error.code], { token: error.token });
}

/** The parsed formula, read back with explicit operators, and its variables. */
export function readFormula(formula: string) {
  const parsed = parseExpression(formula);
  return parsed.ok
    ? { ...parsed, readBack: formatExpression(parsed.ast) }
    : parsed;
}

export function formulaView(
  formula: string,
  inputs: Readonly<Record<string, VariableInput>>,
  unit: AngleUnit,
  strings: Strings,
  fmt: Format
): ViewOutcome {
  const parsed = readFormula(formula);
  if (!parsed.ok) {
    return { kind: "error", message: parseMessage(parsed.error, strings) };
  }
  const variables: { name: string; measured: Measured }[] = [];
  for (const name of parsed.variables) {
    const value = parseMeasuredNumber(inputs[name]?.value ?? "");
    const uncertainty = parseMeasuredNumber(inputs[name]?.uncertainty ?? "");
    if (value === null || uncertainty === null) {
      return { kind: "notice", message: strings.enterAll };
    }
    if (uncertainty < 0) {
      return { kind: "error", message: strings.negativeUncertainty };
    }
    variables.push({ name, measured: { value, uncertainty } });
  }
  const result = propagateFormula(parsed.ast, variables, unit);
  if ("error" in result) {
    return {
      kind: "error",
      message:
        result.error === "not-finite"
          ? strings.notFinite
          : t(strings.noDerivative, { name: result.variable }),
    };
  }
  const terms = result.contributions.map((c) => c.term);
  return {
    kind: "view",
    view: {
      ...result,
      contributions: result.contributions.map((c) => ({
        ...c,
        label: c.name,
        valueText: (inputs[c.name]?.value ?? "").trim(),
        uncertaintyText: (inputs[c.name]?.uncertainty ?? "").trim(),
      })),
      working: [
        `q = ${parsed.readBack} = ${general(fmt, result.value)}`,
        ...result.contributions.map(
          (c) =>
            `∂q/∂${c.name} ≈ ${general(fmt, c.sensitivity)};  |∂q/∂${c.name}| × δ${c.name} = ${general(fmt, c.term)}`
        ),
        `δq = √(${squaresLine(fmt, terms)}) = ${general(fmt, result.uncertainty)}`,
      ],
    },
  };
}
